/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import {
  ArticleEntryData,
  ENTRY_DATA_LICENSEE_TABLE as LICENSEE_TABLE_KEY,
  ENTRY_DATA_DEAL_TERMS_LICENSEE_TABLE_KEYS as LICENSEE_KEYS,
  ENTRY_DATA_LICENSOR_TABLE as LICENSOR_TABLE_KEY,
  ENTRY_DATA_LICENSOR_TABLE_KEYS as LICENSOR_KEYS,
} from "@frontend/common";

import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { useCurrentDealTypeValue } from "@FillInTableContainer/shared/context/CurrentDealTypeContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { isDealSwapRolesSection } from "../utils/isDealSwapRolesSection";

const DEFAULT_ENTRY_DATA_VALUES = {
  value: [],
  options: [],
};

export const useDealSwapRoles = () => {
  const pressId = getPressIdFromURL();
  const [entryData, setEntryData] = useEntryDataState(pressId);
  const articleSection = useArticleSection();

  const changeEntryData = (data: ArticleEntryData) => {
    setEntryData(data as any);
  };

  const { isMergeAndAcquisition } = useCurrentDealTypeValue();

  const swapDealRoles = (
    currentEntryData: ArticleEntryData,
    changeData: (data: ArticleEntryData) => void,
  ) => {
    if (!currentEntryData) return;
    if (!isDealSwapRolesSection(articleSection)) return;

    const sectionData = (currentEntryData as any)[articleSection];
    if (!sectionData) return;

    // NOTE: Licensor Name & R&R
    const licensorTable = sectionData?.[LICENSOR_TABLE_KEY];
    const targetLicensorName =
      licensorTable?.[LICENSOR_KEYS.licensorName] || DEFAULT_ENTRY_DATA_VALUES;
    const targetLicensorRAndR =
      licensorTable?.[LICENSOR_KEYS.licensorResponsibilityRights] ||
      DEFAULT_ENTRY_DATA_VALUES;

    // NOTE: Licensee Name & R&R
    const licenseeTable = sectionData?.[LICENSEE_TABLE_KEY];
    const targetLicenseeName =
      licenseeTable?.at(0)?.[LICENSEE_KEYS.licenseeName] ||
      DEFAULT_ENTRY_DATA_VALUES;
    const targetLicenseeRAndR = isMergeAndAcquisition
      ? licenseeTable?.at(0)?.[LICENSEE_KEYS.mergeAcquisitionTerms]?.[
          LICENSEE_KEYS.licenseResponsibilityRightsMa
        ] || {}
      : licenseeTable?.at(0)?.[
          LICENSEE_KEYS.strategicCollaborationLicenseAgreementTerms
        ]?.[LICENSEE_KEYS.licenseeResponsibilityAndRightsScLa] || {
          value: [],
          options: [],
        };

    // NOTE: M&A only — capture and remap Licensor(MA) / Licensee(MA) arrays before swap
    const rawLicensorMA: any[] = isMergeAndAcquisition
      ? (licensorTable as any)?.["Licensor(MA)"] ?? []
      : [];
    const rawLicenseeMA: any[] = isMergeAndAcquisition
      ? (licenseeTable?.at(0) as any)?.["Licensee(MA)"] ?? []
      : [];

    // Licensor(MA) → Licensee(MA): rename field keys
    const licensorMAAsLicenseeMA = rawLicensorMA.map((item: any) => ({
      ...item,
      "Licensee Name(MA)": item["Licensor Name(MA)"],
      "Licensee Relationship Context(MA)":
        item["Licensor Relationship Context(MA)"],
      "Licensor Name(MA)": undefined,
      "Licensor Relationship Context(MA)": undefined,
    }));

    // Licensee(MA) → Licensor(MA): rename field keys
    const licenseMAAsLicensorMA = rawLicenseeMA.map((item: any) => ({
      ...item,
      "Licensor Name(MA)": item["Licensee Name(MA)"],
      "Licensor Relationship Context(MA)":
        item["Licensee Relationship Context(MA)"],
      "Licensee Name(MA)": undefined,
      "Licensee Relationship Context(MA)": undefined,
    }));

    const changedLicensorTable = {
      ...licensorTable,
      [LICENSOR_KEYS.licensorName]: targetLicenseeName,
      [LICENSOR_KEYS.licensorResponsibilityRights]: targetLicenseeRAndR,
      ...(isMergeAndAcquisition && { "Licensor(MA)": licenseMAAsLicensorMA }),
    };
    const changedLicenseeTable = isMergeAndAcquisition
      ? [
          {
            ...licenseeTable?.at(0),
            [LICENSEE_KEYS.licenseeName]: targetLicensorName,
            "Licensee(MA)": licensorMAAsLicenseeMA,
            [LICENSEE_KEYS.mergeAcquisitionTerms]: {
              ...licenseeTable?.at(0)?.[LICENSEE_KEYS.mergeAcquisitionTerms],
              [LICENSEE_KEYS.licenseResponsibilityRightsMa]:
                targetLicensorRAndR,
            },
          },
        ]
      : [
          {
            ...licenseeTable?.at(0),
            [LICENSEE_KEYS.licenseeName]: targetLicensorName,
            [LICENSEE_KEYS.strategicCollaborationLicenseAgreementTerms]: {
              ...licenseeTable?.at(0)?.[
                LICENSEE_KEYS.strategicCollaborationLicenseAgreementTerms
              ],
              [LICENSEE_KEYS.licenseeResponsibilityAndRightsScLa]:
                targetLicensorRAndR,
            },
          },
        ];

    const swapDealEntryData = {
      ...currentEntryData,
      [articleSection]: {
        ...sectionData,
        [LICENSOR_TABLE_KEY]: changedLicensorTable,
        [LICENSEE_TABLE_KEY]: changedLicenseeTable,
      },
    };

    changeData(swapDealEntryData as any);
  };

  const clickDealSwapButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    swapDealRoles(entryData as ArticleEntryData, changeEntryData);
    // TODO : init_data도 함께 변경이 필요 (Update API 필요 / queryKey 수정 필요)
  };

  return {
    clickDealSwapButton,
  };
};
